if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: ['.product__media-item'],
        };

        this.sliderInstance = false;
        this.thumbsInstance = false;
      }

      connectedCallback() {
        FoxTheme.Motion.inView(this, this.init.bind(this));
      }

      init() {
        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        this.setSliderOptions();

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();

        if (this.enableImageZoom) {
          this.initAmazonLightbox();
        }
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);

        this.sliderOptions = {
          init: false,
          slidesPerView: '1',
          spaceBetween: mediaItemGap,
          loop: false,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'fraction',
          },
          threshold: 2,
        };

        this.thumbsOptions = {
          slidesPerView: 4,
          breakpoints: { 461: { slidesPerView: 5 } },
          spaceBetween: mediaItemGap,
          loop: false,
          freeMode: true,
          watchSlidesProgress: true,
          threshold: 2,
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions = Object.assign({}, this.thumbsOptions, {
              breakpoints: { 768: { direction: 'vertical', slidesPerView: 'auto' } },
            });
            break;
          case 'slider-freemode':
            this.sliderOptions = Object.assign({}, this.sliderOptions, { slidesPerView: 'auto' });
            break;
        }
      }

      updateMediaLayout() {
        if (FoxTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions);
            this.thumbsInstance.init();
            this.sliderOptions.thumbs = { swiper: this.thumbsInstance.slider, autoScrollOffset: 2 };
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs,
          ]);
          this.sliderInstance.init();
          this.handleSliderAfterInit();
          this.handleSlideChange();
          this.sliderInstance.slider.init();
        }
      }

      destroySlider() {
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy();
          this.sliderInstance = false;
        }
      }

      /* ---------- AMAZON-STYLE LIGHTBOX ---------- */
      initAmazonLightbox() {
        // Collect image data from the gallery
        this.mediaData = [];
        const allMedia = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
        allMedia.forEach((media) => {
          const img = media.querySelector('img');
          this.mediaData.push({
            type: media.dataset.mediaType || 'image',
            src: media.dataset.src,
            alt: img ? img.alt : '',
            thumb: img ? img.src : media.dataset.src,
          });
        });

        // Build modal DOM once
        this.buildAmazonModal();

        // Wire up click handlers for opening
        FoxTheme.utils.addEventDelegate({
          selector: '.js-photoswipe--zoom, .amazon-open-gallery',
          context: this,
          handler: (e, el) => {
            e.preventDefault();
            e.stopPropagation();
            const target = el && el.nodeType ? el : e.target;
            const mediaItem =
              target.closest('.product__media-item') || target.closest('[data-media-index]');
            const idx = mediaItem ? parseInt(mediaItem.dataset.mediaIndex, 10) || 0 : 0;
            this.openAmazonModal(idx);
          },
        });
      }

      buildAmazonModal() {
        // Get product data from the page
        const productTitle =
          document.querySelector('.product__title h1, .product__title, [class*="product__title"]')?.innerText?.trim() ||
          document.title;

        // Try to read selected variant options for right panel
        const optionsHtml = this.getSelectedOptionsHtml();

        const modal = document.createElement('div');
        modal.className = 'amazon-lightbox';
        modal.setAttribute('aria-hidden', 'true');
        modal.innerHTML = `
          <div class="amazon-lightbox__inner">
            <div class="amazon-lightbox__tabs">
              <h2 class="amazon-lightbox__product-name">${productTitle}</h2>
            </div>
            <button class="amazon-lightbox__close" aria-label="Close">
              <svg viewBox="0 0 24 24" width="22" height="22" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>

            <div class="amazon-lightbox__body">
              <div class="amazon-lightbox__stage">
                <button class="amazon-lightbox__nav amazon-lightbox__nav--prev" aria-label="Previous">
                  <svg viewBox="0 0 24 24" width="28" height="28" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
                </button>
                <div class="amazon-lightbox__img-wrapper">
                  <img class="amazon-lightbox__main-img" alt="">
                </div>
                <button class="amazon-lightbox__nav amazon-lightbox__nav--next" aria-label="Next">
                  <svg viewBox="0 0 24 24" width="28" height="28" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
                </button>
              </div>

              <aside class="amazon-lightbox__side">
                <div class="amazon-lightbox__options">${optionsHtml}</div>
                <div class="amazon-lightbox__thumbs"></div>
              </aside>
            </div>
          </div>
        `;
        document.body.appendChild(modal);
        this.amazonModal = modal;

        // Populate thumbnails
        const thumbsWrap = modal.querySelector('.amazon-lightbox__thumbs');
        this.mediaData.forEach((item, i) => {
          const t = document.createElement('button');
          t.type = 'button';
          t.className = 'amazon-lightbox__thumb';
          t.dataset.index = i;
          t.innerHTML =
            item.type === 'image'
              ? `<img src="${item.thumb}" alt="${item.alt}">`
              : `<span class="amazon-lightbox__thumb-icon">${item.type === 'model' ? '3D' : '▶'}</span>`;
          t.addEventListener('click', () => this.showAmazonSlide(i));
          thumbsWrap.appendChild(t);
        });

        // Close handlers
        modal.querySelector('.amazon-lightbox__close').addEventListener('click', () => this.closeAmazonModal());
        modal.addEventListener('click', (e) => {
          if (e.target === modal) this.closeAmazonModal();
        });

        // Nav
        modal.querySelector('.amazon-lightbox__nav--prev').addEventListener('click', () =>
          this.showAmazonSlide(this.currentIndex - 1)
        );
        modal.querySelector('.amazon-lightbox__nav--next').addEventListener('click', () =>
          this.showAmazonSlide(this.currentIndex + 1)
        );

        // Keyboard
        document.addEventListener('keydown', (e) => {
          if (!this.amazonModal.classList.contains('is-open')) return;
          if (e.key === 'Escape') this.closeAmazonModal();
          if (e.key === 'ArrowLeft') this.showAmazonSlide(this.currentIndex - 1);
          if (e.key === 'ArrowRight') this.showAmazonSlide(this.currentIndex + 1);
        });
      }

      getSelectedOptionsHtml() {
        // Try to read selected variant options from the product form
        const html = [];
        const form = document.querySelector('product-form form, form[action*="/cart/add"]');
        if (form) {
          const optionSelectors = form.querySelectorAll('[data-option-position], .product-form__input');
          const seen = new Set();
          optionSelectors.forEach((wrap) => {
            const label = wrap.querySelector('legend, .form__label, label');
            const selectedInput = wrap.querySelector('input[type="radio"]:checked, select');
            if (label && selectedInput) {
              const name = label.innerText.trim().split(':')[0];
              const val =
                selectedInput.tagName === 'SELECT'
                  ? selectedInput.options[selectedInput.selectedIndex].text
                  : selectedInput.value;
              const key = name + ':' + val;
              if (!seen.has(key)) {
                seen.add(key);
                html.push(`<div class="amazon-lightbox__option"><strong>${name}:</strong> ${val}</div>`);
              }
            }
          });
        }
        return html.join('');
      }

      openAmazonModal(index) {
        this.currentIndex = index || 0;
        this.amazonModal.classList.remove('is-closing');
        this.amazonModal.classList.add('is-open');
        this.amazonModal.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';

        // Show the correct image (no slide animation on first open)
        const item = this.mediaData[this.currentIndex];
        const img = this.amazonModal.querySelector('.amazon-lightbox__main-img');
        img.src = item.src;
        img.alt = item.alt || '';
        img.className = 'amazon-lightbox__main-img';

        // Highlight active thumb
        this.amazonModal.querySelectorAll('.amazon-lightbox__thumb').forEach((t, i) => {
          t.classList.toggle('is-active', i === this.currentIndex);
        });
      }

      closeAmazonModal() {
        this.amazonModal.classList.add('is-closing');
        this.amazonModal.classList.remove('is-open');

        setTimeout(() => {
          this.amazonModal.classList.remove('is-closing');
          this.amazonModal.setAttribute('aria-hidden', 'true');
          document.body.style.overflow = '';
        }, 350);

        // sync main gallery to whatever image was last viewed
        if (this.sliderInstance && this.sliderInstance.slider) {
          this.sliderInstance.slider.slideToLoop(this.currentIndex, 0, false);
        }
      }

      showAmazonSlide(index) {
        if (index < 0) index = this.mediaData.length - 1;
        if (index >= this.mediaData.length) index = 0;

        const oldIndex = this.currentIndex;
        this.currentIndex = index;
        const item = this.mediaData[index];
        const img = this.amazonModal.querySelector('.amazon-lightbox__main-img');

        // Determine slide direction
        const goingRight =
          index > oldIndex || (oldIndex === this.mediaData.length - 1 && index === 0);
        // Handle wrap-around edge case (last -> first goes right, first -> last goes left)
        const isWrapForward = oldIndex === this.mediaData.length - 1 && index === 0;
        const isWrapBackward = oldIndex === 0 && index === this.mediaData.length - 1;
        const direction = isWrapForward
          ? 'right'
          : isWrapBackward
          ? 'left'
          : index > oldIndex
          ? 'right'
          : 'left';

        // Remove old animation classes
        img.classList.remove('slide-in-right', 'slide-in-left', 'slide-out-right', 'slide-out-left');

        // Force reflow so animation restarts
        void img.offsetWidth;

        // Add slide-in animation based on direction
        if (direction === 'right') {
          img.classList.add('slide-in-right');
        } else {
          img.classList.add('slide-in-left');
        }

        img.src = item.src;
        img.alt = item.alt || '';

        // Highlight active thumb
        this.amazonModal.querySelectorAll('.amazon-lightbox__thumb').forEach((t, i) => {
          t.classList.toggle('is-active', i === index);
        });

        // Auto-scroll active thumb into view
        const activeThumb = this.amazonModal.querySelector('.amazon-lightbox__thumb.is-active');
        if (activeThumb) {
          activeThumb.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }

      /* ---------- END LIGHTBOX ---------- */

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;
          if (slides[activeIndex]) {
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      handleSlideChange() {
        this.sliderInstance.slider.on('realIndexChange', (swiper) => {
          const { slides, activeIndex, thumbs } = swiper;
          if (thumbs.swiper) thumbs.swiper.slideTo(activeIndex);
          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      setActiveMedia(variant) {
        if (!variant || !variant.hasOwnProperty('featured_media') || !variant.featured_media) return;
        if (this.sliderInstance.slider) {
          const slideIndex = variant.featured_media.position || 0;
          this.sliderInstance.slider.slideToLoop(slideIndex - 1, 0, false);
        } else {
          this.sortMediaItems(variant);
        }
      }

      sortMediaItems(variant) {
        let newMedias = this.elements.mediaItems;
        newMedias.sort((a, b) => a.dataset.mediaIndex - b.dataset.mediaIndex);
        newMedias.some((media, index) => {
          if (media.dataset.mediaId == variant.featured_media.id) {
            const [element] = newMedias.splice(index, 1);
            newMedias.unshift(element);
            return true;
          }
        });
        this.elements.mediaList.innerHTML = '';
        newMedias.forEach((media) => this.elements.mediaList.appendChild(media));

        if (!FoxTheme.config.mqlMobile) {
          const selectedMedia = this.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
          if (selectedMedia) window.scrollTo({ top: selectedMedia.offsetTop, behavior: 'smooth' });
        }
      }
    }
  );
}